import type { ReactNode } from "react";
import { searchableSetting } from "./settingsSearch";

import {
  DEFAULT_UNIFIED_SETTINGS,
  MAX_GLASS_OPACITY,
  MIN_GLASS_OPACITY,
  type SidebarLayoutMode,
} from "@cmmd-forge/contracts";

import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { useTheme } from "../../hooks/useTheme";

import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import {
  SettingResetButton,
  SettingsBlock,
  SettingsPageContainer,
  SettingsRow,
  SettingsSection,
} from "./settingsLayout";
import { AppearanceModePicker } from "./ThemePicker";
import { AppearanceThemeSection, ContrastRow } from "./AppearanceThemeSection";
import { TypographySection } from "./TypographySettings";

const SIDEBAR_LAYOUT_MODE_LABELS: Record<SidebarLayoutMode, string> = {
  activity: "Activity",
  projects: "Projects",
};

const SIDEBAR_LAYOUT_MODE_OPTIONS: readonly SidebarLayoutMode[] = ["activity", "projects"];

function isSidebarLayoutMode(value: unknown): value is SidebarLayoutMode {
  return (
    typeof value === "string" && (SIDEBAR_LAYOUT_MODE_OPTIONS as readonly string[]).includes(value)
  );
}

function SidebarLayoutModeControl(props: {
  readonly mode: SidebarLayoutMode;
  readonly onModeChange: (mode: SidebarLayoutMode) => void;
}): ReactNode {
  return (
    <Select
      value={props.mode}
      onValueChange={(value) => {
        if (isSidebarLayoutMode(value)) {
          props.onModeChange(value);
        }
      }}
    >
      <SelectTrigger className="w-full sm:w-40" aria-label="Sidebar layout">
        <SelectValue>{SIDEBAR_LAYOUT_MODE_LABELS[props.mode]}</SelectValue>
      </SelectTrigger>
      <SelectPopup align="end" alignItemWithTrigger={false}>
        {SIDEBAR_LAYOUT_MODE_OPTIONS.map((option) => (
          <SelectItem key={option} hideIndicator value={option}>
            {SIDEBAR_LAYOUT_MODE_LABELS[option]}
          </SelectItem>
        ))}
      </SelectPopup>
    </Select>
  );
}

/**
 * Its own component so the Appearance panel stays a list of rows rather than
 * a function that also knows how a slider is built.
 */
function GlassOpacityRow() {
  const glassOpacity = useSettings((settings) => settings.glassOpacity);
  const { updateSettings } = useUpdateSettings();

  return (
    <SettingsRow
      {...searchableSetting("appearance-glass-opacity")}
      description="How solid glass surfaces are. Higher values make menus, dialogs, and the composer read more like panels and less like the canvas behind them."
      resetAction={
        glassOpacity !== DEFAULT_UNIFIED_SETTINGS.glassOpacity ? (
          <SettingResetButton
            label="glass opacity"
            onClick={() => updateSettings({ glassOpacity: DEFAULT_UNIFIED_SETTINGS.glassOpacity })}
          />
        ) : null
      }
      control={
        <div className="flex w-full items-center gap-3 sm:w-52">
          <output
            className="min-w-12 rounded-md bg-muted px-2 py-1 text-center font-medium font-mono text-foreground text-xs tabular-nums"
            htmlFor="glass-opacity"
          >
            {glassOpacity}%
          </output>
          <input
            aria-label="Glass opacity"
            // A range input is ~16px tall by default, which is not a touch
            // target. `min-h-touch` grows the hit area below `md` without
            // changing the track: the thumb is what you grab, and the row it
            // sits in is the same height either way.
            className="min-h-touch min-w-0 flex-1 md:min-h-0"
            id="glass-opacity"
            max={MAX_GLASS_OPACITY}
            min={MIN_GLASS_OPACITY}
            onChange={(event) => {
              const next = Number(event.currentTarget.value);
              if (Number.isInteger(next)) updateSettings({ glassOpacity: next });
            }}
            step={5}
            type="range"
            value={glassOpacity}
          />
        </div>
      }
    />
  );
}

/**
 * Its own component for the same reason `GlassOpacityRow` is: the panel stays a
 * list of rows rather than a function that also knows which setting a switch
 * reads and what its default is.
 */
function ComposerCollapseOnBlurRow() {
  const collapseOnBlur = useSettings((settings) => settings.composerCollapseOnBlur);
  const { updateSettings } = useUpdateSettings();

  return (
    <SettingsRow
      {...searchableSetting("appearance-composer-collapse-on-blur")}
      description="Losing editor focus rests the Composer on an existing thread, and scrolling up still does when this is off."
      resetAction={
        collapseOnBlur !== DEFAULT_UNIFIED_SETTINGS.composerCollapseOnBlur ? (
          <SettingResetButton
            label="collapse composer on blur"
            onClick={() =>
              updateSettings({
                composerCollapseOnBlur: DEFAULT_UNIFIED_SETTINGS.composerCollapseOnBlur,
              })
            }
          />
        ) : null
      }
      control={
        <Switch
          aria-label="Collapse composer on blur"
          checked={collapseOnBlur}
          onCheckedChange={(checked) =>
            updateSettings({ composerCollapseOnBlur: Boolean(checked) })
          }
        />
      }
    />
  );
}

/**
 * The sidebar v2 escape hatch: true restores forge's v1 per-project tree.
 * Mirrors upstream's own "Sidebar (legacy)" toggle.
 */
function LegacySidebarRow() {
  const legacySidebarEnabled = useSettings((settings) => settings.legacySidebarEnabled);
  const { updateSettings } = useUpdateSettings();

  return (
    <SettingsRow
      {...searchableSetting("appearance-sidebar-legacy")}
      description="Restore the legacy per-project thread tree instead of the default sidebar."
      resetAction={
        legacySidebarEnabled !== DEFAULT_UNIFIED_SETTINGS.legacySidebarEnabled ? (
          <SettingResetButton
            label="legacy sidebar"
            onClick={() =>
              updateSettings({
                legacySidebarEnabled: DEFAULT_UNIFIED_SETTINGS.legacySidebarEnabled,
              })
            }
          />
        ) : null
      }
      control={
        <Switch
          aria-label="Sidebar (legacy)"
          checked={legacySidebarEnabled}
          onCheckedChange={(checked) => updateSettings({ legacySidebarEnabled: Boolean(checked) })}
        />
      }
    />
  );
}

/** Render appearance controls, including the legacy sidebar switch, with per-setting reset actions. */
export function AppearanceSettingsPanel() {
  const {
    theme,
    setTheme,
    themeId,
    setThemeId,
    resolvedTheme,
    themeHalves,
    setThemeHalf,
    clearThemeHalves,
  } = useTheme();
  const sidebarLayoutMode = useSettings((s) => s.sidebarLayoutMode);
  const { updateSettings } = useUpdateSettings();

  return (
    <SettingsPageContainer>
      <SettingsSection title="Appearance">
        {/* Both pickers are blocks, not rows. A grid of app miniatures is the
            setting — it is not a control that belongs beside a label, and the
            reference lays both of them out this way. Putting the colour-scheme
            picker in a row's control cell sized it by its captions' max-content
            (~208px, pinned right) while the theme grid ran the full column, so
            the page showed the same tile at two widths. The theme half of the
            page (palette, mix, custom themes, contrast) lives in
            `AppearanceThemeSection` so this panel stays a composition. */}
        <SettingsBlock
          {...searchableSetting("appearance-colour-scheme")}
          description="Follow the system appearance, or pin light or dark for this device."
          resetAction={
            theme !== DEFAULT_UNIFIED_SETTINGS.theme ? (
              <SettingResetButton
                label="colour scheme"
                onClick={() => setTheme(DEFAULT_UNIFIED_SETTINGS.theme)}
              />
            ) : null
          }
        >
          <AppearanceModePicker mode={theme} themeId={themeId} onModeChange={setTheme} />
        </SettingsBlock>
        <AppearanceThemeSection
          themeId={themeId}
          resolvedTheme={resolvedTheme === "dark" ? "dark" : "light"}
          themeHalves={themeHalves}
          onThemeIdChange={setThemeId}
          onSetHalf={setThemeHalf}
          onClearHalves={clearThemeHalves}
        />
        <GlassOpacityRow />
        <ContrastRow />
        <SettingsRow
          {...searchableSetting("appearance-sidebar-layout")}
          description="Activity lists your threads newest first and uses projects as a filter. Projects gives each project its own row with its threads nested underneath, plus runtime controls."
          resetAction={
            sidebarLayoutMode !== DEFAULT_UNIFIED_SETTINGS.sidebarLayoutMode ? (
              <SettingResetButton
                label="sidebar layout"
                onClick={() =>
                  updateSettings({
                    sidebarLayoutMode: DEFAULT_UNIFIED_SETTINGS.sidebarLayoutMode,
                  })
                }
              />
            ) : null
          }
          control={
            <SidebarLayoutModeControl
              mode={sidebarLayoutMode}
              onModeChange={(mode: SidebarLayoutMode) =>
                updateSettings({ sidebarLayoutMode: mode })
              }
            />
          }
        />
        <ComposerCollapseOnBlurRow />
        <LegacySidebarRow />
      </SettingsSection>

      <TypographySection />
    </SettingsPageContainer>
  );
}
